import React, { useState } from "react";
import {
  Phone,
  MessageCircle,
  Wrench,
  Zap,
  Truck,
  Lock,
  Clock,
  ShieldCheck,
  Euro,
  CheckCircle2,
  MapPin,
  ArrowRight,
  Star,
  ClipboardList,
  AlertTriangle,
  FileText,
} from "lucide-react";
import {
  COMPANY_NAME,
  LEGAL_NAME,
  VAT_NUMBER,
  COMPANY_ADDRESS,
  PEC_EMAIL,
  PHONE_DISPLAY,
  telLink,
  whatsappLink,
  EMAIL,
} from "@/lib/contact";
import { REVIEWS, REVIEWS_SUMMARY } from "@/lib/reviews";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hero image: technicians + van (replace easily)
const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_soccorso-casa/artifacts/xwnubp0j_WhatsApp%20Image%202026-05-21%20at%2017.57.38.jpeg";

// ============================================================
// HEADER
// ============================================================
function Header() {
  const navItems = [
    { href: "#servizi", label: "Servizi" },
    { href: "#prezzi", label: "Prezzi" },
    { href: "#zone", label: "Zone" },
    { href: "#richiedi", label: "Contatti" },
  ];

  return (
    <header
      className="sticky top-0 z-40 bg-[#0B1F33]/95 backdrop-blur-md text-white shadow-sm"
      data-testid="site-header"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl flex items-center justify-between h-16 md:h-20 gap-3">
        {/* Logo */}
        <a href="#top" className="flex items-center gap-2.5 shrink-0" data-testid="logo-link">
          <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#FF6A2A] text-white shadow-sm">
            <ShieldCheck className="w-5 h-5" strokeWidth={2.5} />
          </span>
          <div className="leading-tight">
            <div className="font-display font-black text-sm sm:text-base tracking-tight uppercase">
              Pronto Intervento
              <br className="sm:hidden" />
              <span className="text-[#FF6A2A] sm:ml-1">24H</span>
            </div>
          </div>
        </a>

        {/* Nav */}
        <nav className="hidden lg:flex items-center gap-10" data-testid="main-nav">
          {navItems.map((it) => (
            <a
              key={it.href}
              href={it.href}
              className="text-[15px] font-semibold text-white/85 hover:text-[#FF6A2A] transition-colors"
              data-testid={`nav-${it.label.toLowerCase()}`}
            >
              {it.label}
            </a>
          ))}
        </nav>

        {/* CTA */}
        <a
          href={telLink}
          className="hidden md:inline-flex items-center gap-3 bg-[#FF6A2A] hover:bg-[#E55A1A] text-white px-5 py-2.5 rounded-lg transition-all shadow-md hover:shadow-lg"
          data-testid="header-call-button"
        >
          <span className="flex items-center justify-center w-9 h-9 rounded-md bg-white/15">
            <Phone className="w-4 h-4" strokeWidth={2.8} />
          </span>
          <span className="leading-tight">
            <span className="block font-black text-[15px]">{PHONE_DISPLAY}</span>
            <span className="block text-[10px] font-semibold uppercase tracking-wider opacity-90">
              Disponibili 24/7
            </span>
          </span>
        </a>

        <a
          href={telLink}
          className="md:hidden inline-flex items-center gap-1.5 bg-[#FF6A2A] text-white font-bold px-3 py-2 rounded-lg text-sm"
          data-testid="header-call-button-mobile"
        >
          <Phone className="w-4 h-4" strokeWidth={2.8} />
          Chiama
        </a>
      </div>
    </header>
  );
}

// ============================================================
// HERO
// ============================================================
function Hero() {
  return (
    <section
      id="top"
      className="relative bg-[#0B1F33] text-white overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background image (right side) */}
      <div className="absolute inset-y-0 right-0 w-full lg:w-3/5">
        <img
          src={HERO_IMG}
          alt="Tecnici Pronto Intervento con furgone"
          className="w-full h-full object-cover object-center"
        />
        {/* Gradient mask: navy on left fading to clear on right so image stays visible */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1F33] via-[#0B1F33]/70 lg:via-[#0B1F33]/20 to-transparent" />
      </div>
      {/* Solid navy left (mobile fallback) */}
      <div className="absolute inset-y-0 left-0 w-full lg:w-1/2 bg-gradient-to-r from-[#0B1F33] via-[#0B1F33]/90 to-[#0B1F33]/60 lg:to-transparent" />

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-14 sm:py-20 lg:py-28">
        <div className="max-w-2xl fade-up">
          {/* Badge */}
          <div
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold uppercase tracking-wider text-[11px] px-3 py-1.5 rounded-full mb-7"
            data-testid="hero-badge"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF6A2A] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF6A2A]"></span>
            </span>
            Disponibili 24/7
          </div>

          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-5 text-white">
            Pronto Intervento <span className="text-[#FF6A2A]">24H</span>
            <br />
            Arriviamo entro <span className="text-[#FF6A2A]">30–60 minuti</span>
          </h1>

          <p className="text-base sm:text-lg text-white/80 mb-9 max-w-xl leading-relaxed">
            Idraulico, elettricista, spurghi e apertura porte blindate a{" "}
            <span className="text-[#FF6A2A] font-semibold">Parma, Piacenza e Modena</span>.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-12">
            <a
              href={telLink}
              className="group inline-flex items-center gap-3 bg-[#FF6A2A] hover:bg-[#E55A1A] text-white px-5 sm:px-6 py-4 rounded-xl shadow-lg shadow-[#FF6A2A]/20 transition-all hover:-translate-y-0.5"
              data-testid="hero-call-button"
            >
              <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-white/15">
                <Phone className="w-5 h-5" strokeWidth={2.6} />
              </span>
              <span className="leading-tight text-left">
                <span className="block font-black text-base sm:text-lg uppercase tracking-wide">
                  Chiama Ora
                </span>
                <span className="block text-xs sm:text-sm font-semibold opacity-95">
                  {PHONE_DISPLAY}
                </span>
              </span>
            </a>

            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1DA851] text-white px-5 sm:px-6 py-4 rounded-xl shadow-lg transition-all hover:-translate-y-0.5"
              data-testid="hero-whatsapp-button"
            >
              <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-white/15">
                <MessageCircle className="w-5 h-5" strokeWidth={2.4} />
              </span>
              <span className="leading-tight text-left">
                <span className="block font-black text-base sm:text-lg uppercase tracking-wide">
                  WhatsApp
                </span>
                <span className="block text-xs sm:text-sm font-semibold opacity-95">
                  Immediato
                </span>
              </span>
            </a>
          </div>

          {/* Trust badges row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 max-w-2xl">
            {[
              {
                icon: Clock,
                title: "Interventi rapidi",
                sub: "entro 30–60 minuti",
              },
              {
                icon: ShieldCheck,
                title: "Tecnici qualificati",
                sub: "e professionali",
              },
              {
                icon: Euro,
                title: "Prezzi chiari",
                sub: "senza sorprese",
              },
            ].map(({ icon: Icon, title, sub }, i) => (
              <div
                key={i}
                className="flex items-center gap-3 text-white"
                data-testid={`trust-badge-${i}`}
              >
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-white/10 border border-white/15 shrink-0">
                  <Icon className="w-4 h-4 text-[#FF6A2A]" strokeWidth={2.2} />
                </span>
                <div className="leading-tight">
                  <div className="text-sm font-bold">{title}</div>
                  <div className="text-xs text-white/65">{sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// SERVICES
// ============================================================
const SERVICES = [
  {
    id: "idraulico",
    icon: Wrench,
    title: "Idraulico",
    desc: "Riparazioni perdite, tubature, scaldabagni, sanitari e impianti idraulici.",
    img: "https://images.unsplash.com/photo-1676210133055-eab6ef033ce3?auto=format&fit=crop&w=900&q=80",
  },
  {
    id: "elettricista",
    icon: Zap,
    title: "Elettricista",
    desc: "Guasti elettrici, quadri, prese, luci, cortocircuiti e molto altro.",
    img: "https://images.pexels.com/photos/33694019/pexels-photo-33694019.jpeg?auto=compress&cs=tinysrgb&w=900",
  },
  {
    id: "spurghi",
    icon: Truck,
    title: "Spurghi",
    desc: "Spurghi di pozzi neri, fosse biologiche, tubazioni e sturate rapide.",
    img: "https://images.pexels.com/photos/6419128/pexels-photo-6419128.jpeg?auto=compress&cs=tinysrgb&w=900",
  },
  {
    id: "serrature",
    icon: Lock,
    title: "Apertura Porte Blindate",
    desc: "Apertura porte blindate senza scasso, sostituzione serrature e cilindri.",
    img: "https://images.unsplash.com/photo-1562259929-b4e1fd3aef09?auto=format&fit=crop&w=900&q=80",
  },
];

function ServicesSection() {
  return (
    <section id="servizi" className="bg-white py-16 md:py-24" data-testid="services-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="text-[#FF6A2A] font-bold uppercase tracking-[0.2em] text-xs">
            I Nostri Servizi
          </span>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-[#0B1F33] mt-3 leading-tight">
            Sempre <span className="text-[#FF6A2A]">pronti</span>, sempre al tuo fianco.
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {SERVICES.map((s) => {
            const Icon = s.icon;
            return (
              <article
                key={s.id}
                className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-[#FF6A2A]/50 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
                data-testid={`service-card-${s.id}`}
              >
                {/* Image */}
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={s.img}
                    alt={s.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F33]/40 via-transparent to-transparent" />
                  {/* Floating icon badge */}
                  <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex items-center justify-center w-14 h-14 rounded-full bg-white border-2 border-[#FF6A2A] text-[#FF6A2A] shadow-md group-hover:bg-[#FF6A2A] group-hover:text-white transition-all">
                    <Icon className="w-6 h-6" strokeWidth={2} />
                  </div>
                </div>

                {/* Content */}
                <div className="pt-10 pb-7 px-6 text-center flex flex-col flex-1">
                  <h3 className="font-display font-bold text-xl text-[#0B1F33] mb-3">
                    {s.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-5 flex-1">{s.desc}</p>
                  <a
                    href={telLink}
                    className="text-[#FF6A2A] font-bold text-sm inline-flex items-center justify-center gap-1.5 hover:gap-3 transition-all"
                    data-testid={`service-call-${s.id}`}
                  >
                    Scopri di più <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// PRICING
// ============================================================
function PricingSection() {
  const cards = [
    { icon: Phone, price: "€70", label: "Diritto di chiamata", desc: "Incluso sopralluogo e diagnosi del problema." },
    { icon: Lock, price: "da €100", label: "Serrature", desc: "Apertura porte blindate da €100." },
    { icon: Truck, price: "da €150", label: "Spurghi", desc: "Prezzi trasparenti e senza sorprese." },
    { icon: FileText, price: "Preventivo", label: "Prima del lavoro", desc: "Ti comunichiamo sempre il prezzo prima di iniziare." },
  ];

  return (
    <section id="prezzi" className="bg-slate-50 py-16 md:py-24" data-testid="pricing-transparency">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="text-[#FF6A2A] font-bold uppercase tracking-[0.2em] text-xs">
            Prezzi Chiari, Senza Sorprese
          </span>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-[#0B1F33] mt-3 leading-tight">
            Preventivo comunicato prima di ogni intervento.
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <div
                key={i}
                className="bg-white border border-[#FF6A2A]/25 rounded-2xl p-7 text-center hover:border-[#FF6A2A] hover:shadow-xl transition-all"
                data-testid={`pricing-item-${i}`}
              >
                <div className="mx-auto flex items-center justify-center w-16 h-16 rounded-full border-2 border-[#FF6A2A] text-[#FF6A2A] mb-5">
                  <Icon className="w-7 h-7" strokeWidth={2} />
                </div>
                <div className="font-display font-black text-3xl sm:text-4xl text-[#FF6A2A] leading-none mb-2">
                  {c.price}
                </div>
                <div className="font-display font-bold text-lg text-[#0B1F33] mb-2">
                  {c.label}
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">{c.desc}</p>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-10 inline-flex items-center justify-center gap-2 text-slate-600 text-sm font-semibold w-full">
          <ShieldCheck className="w-4 h-4 text-[#FF6A2A]" strokeWidth={2.5} />
          Nessun costo nascosto. Massima trasparenza.
        </div>
      </div>
    </section>
  );
}

// ============================================================
// HOW IT WORKS
// ============================================================
function HowItWorks() {
  const steps = [
    { icon: Phone, title: "Ci chiami", desc: "Raccontaci il problema e la tua esigenza." },
    { icon: Truck, title: "Arriviamo da te", desc: "Entro 30–60 minuti siamo sul posto." },
    { icon: ClipboardList, title: "Preventivo chiaro", desc: "Ti diciamo subito il costo prima di intervenire." },
    { icon: Wrench, title: "Intervento risolto", desc: "Lavoro completato con garanzia e massima professionalità." },
  ];
  return (
    <section className="bg-white py-16 md:py-24" data-testid="how-it-works">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#FF6A2A] font-bold uppercase tracking-[0.2em] text-xs">
            Come Funziona
          </span>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-[#0B1F33] mt-3 leading-tight">
            Semplice, veloce, efficace.
          </h2>
        </div>

        <div className="relative grid sm:grid-cols-2 lg:grid-cols-4 gap-y-12 lg:gap-x-8">
          {/* Dashed connector line (desktop only) */}
          <div className="hidden lg:block absolute top-12 left-[12%] right-[12%] border-t-2 border-dashed border-[#FF6A2A]/30 -z-0" />

          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="relative text-center px-4" data-testid={`step-${i}`}>
                <div className="relative mx-auto w-24 h-24 mb-5">
                  <div className="absolute inset-0 rounded-full bg-white border-2 border-[#FF6A2A] flex items-center justify-center text-[#FF6A2A]">
                    <Icon className="w-10 h-10" strokeWidth={1.8} />
                  </div>
                  <div className="absolute -top-1 -right-1 flex items-center justify-center w-9 h-9 rounded-full bg-[#FF6A2A] text-white font-black text-base shadow-md">
                    {i + 1}
                  </div>
                </div>
                <h3 className="font-display font-bold text-lg text-[#0B1F33] mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed max-w-[220px] mx-auto">
                  {s.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// MEET GIULIO
// ============================================================
function TechSection() {
  const traits = [
    "Tecnico certificato",
    "Furgone attrezzato 24/7",
    "Esperienza decennale",
    "Diagnosi rapida e onesta",
  ];
  return (
    <section id="giulio" className="bg-slate-50 py-16 md:py-24" data-testid="tech-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="absolute -inset-3 sm:-inset-4 bg-[#FF6A2A]/15 rounded-3xl rotate-[-2deg]" />
            <div className="relative rounded-3xl overflow-hidden shadow-xl border-4 border-white aspect-[3/4] max-w-md mx-auto lg:mx-0">
              <img
                src="https://customer-assets.emergentagent.com/job_soccorso-casa/artifacts/mz13j8w5_sito%20foto.jpeg"
                alt="Giulio, tecnico Pronto Intervento 24H"
                className="w-full h-full object-cover"
                loading="lazy"
                data-testid="giulio-photo"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0B1F33] via-[#0B1F33]/70 to-transparent p-5">
                <div className="text-[#FF6A2A] font-bold text-xs uppercase tracking-widest">In servizio · 24/7</div>
                <div className="font-display font-black text-white text-2xl">Giulio</div>
                <div className="text-white/80 text-sm">Tecnico Senior · Fidenza (PR)</div>
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <span className="text-[#FF6A2A] font-bold uppercase tracking-[0.2em] text-xs">
              Il Nostro Tecnico
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-[#0B1F33] mt-3 mb-5 leading-tight">
              Ciao, sono <span className="text-[#FF6A2A]">Giulio</span>.<br />
              Risolvo la tua emergenza.
            </h2>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed mb-7 max-w-xl">
              Da oltre 10 anni intervengo a domicilio in Emilia per guasti idraulici, elettrici, spurghi e porte bloccate. Arrivo con il furgone attrezzato per risolvere subito, senza rinvii e senza brutte sorprese in fattura.
            </p>

            <ul className="grid grid-cols-2 gap-3 mb-8 max-w-md">
              {traits.map((t, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm font-semibold text-[#0B1F33]"
                  data-testid={`giulio-trait-${i}`}
                >
                  <CheckCircle2 className="w-5 h-5 text-[#FF6A2A] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                  {t}
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={telLink}
                className="inline-flex items-center justify-center gap-2 bg-[#FF6A2A] hover:bg-[#E55A1A] text-white font-bold px-6 py-4 rounded-xl shadow-lg transition-all hover:-translate-y-0.5"
                data-testid="giulio-call-button"
              >
                <Phone className="w-5 h-5" strokeWidth={2.5} />
                Chiama Giulio Ora
              </a>
              <a
                href={whatsappLink("Ciao Giulio, ho bisogno di un pronto intervento.")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-white border-2 border-[#0B1F33] hover:bg-slate-100 text-[#0B1F33] font-bold px-6 py-4 rounded-xl transition-all"
                data-testid="giulio-whatsapp-button"
              >
                <MessageCircle className="w-5 h-5" strokeWidth={2.5} />
                Scrivi su WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// REVIEWS (navy bg)
// ============================================================
function ReviewsSection() {
  const top = REVIEWS.slice(0, 3);
  return (
    <section
      id="recensioni"
      className="bg-[#0B1F33] text-white py-16 md:py-24 relative overflow-hidden"
      data-testid="reviews-section"
    >
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-[#FF6A2A]/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-[#FF6A2A]/10 rounded-full blur-3xl" />

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="text-[#FF6A2A] font-bold uppercase tracking-[0.2em] text-xs">
            Dicono di Noi
          </span>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight">
            La soddisfazione dei nostri clienti.
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
          {top.map((r, i) => (
            <article
              key={i}
              className="bg-white text-slate-800 rounded-2xl p-6 shadow-lg flex flex-col"
              data-testid={`review-card-${i}`}
            >
              <div className="flex gap-0.5 mb-4">
                {[...Array(r.rating)].map((_, idx) => (
                  <Star key={idx} className="w-5 h-5 fill-[#FF6A2A] text-[#FF6A2A]" />
                ))}
              </div>
              <p className="italic text-[15px] leading-relaxed text-slate-700 mb-5 flex-1">
                "{r.text}"
              </p>
              <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                <div className="flex items-center justify-center w-11 h-11 rounded-full bg-[#0B1F33] text-white font-black">
                  {r.name.charAt(0)}
                </div>
                <div className="leading-tight">
                  <div className="font-bold text-[#0B1F33] text-sm">{r.name}</div>
                  <div className="text-xs text-slate-500">{r.city}</div>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div
          className="text-center mt-10 text-white/80 text-base font-semibold"
          data-testid="reviews-summary"
        >
          Oltre {REVIEWS_SUMMARY.totalReviews}+ clienti soddisfatti in tutta la nostra area di intervento.
        </div>
      </div>
    </section>
  );
}

// ============================================================
// COVERAGE
// ============================================================
function CoverageSection() {
  const cities = ["Parma e provincia", "Piacenza e provincia", "Modena e provincia"];
  return (
    <section id="zone" className="bg-white py-16 md:py-24" data-testid="coverage-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <span className="text-[#FF6A2A] font-bold uppercase tracking-[0.2em] text-xs">
              Dove Interveniamo
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-[#0B1F33] mt-3 mb-7 leading-tight">
              Parma, Piacenza e Modena<br />
              <span className="text-[#FF6A2A]">e provincia.</span>
            </h2>
            <ul className="space-y-4 mb-8">
              {cities.map((c, i) => (
                <li
                  key={i}
                  className="flex items-center gap-3 text-base font-semibold text-[#0B1F33]"
                  data-testid={`coverage-city-${i}`}
                >
                  <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#FF6A2A]/15 text-[#FF6A2A]">
                    <MapPin className="w-4 h-4" strokeWidth={2.5} />
                  </span>
                  {c}
                </li>
              ))}
            </ul>
            <a
              href={telLink}
              className="inline-flex items-center gap-2 bg-[#FF6A2A] hover:bg-[#E55A1A] text-white font-bold px-6 py-4 rounded-xl shadow-lg transition-all hover:-translate-y-0.5"
              data-testid="coverage-call-button"
            >
              <Phone className="w-5 h-5" strokeWidth={2.5} />
              Chiama nella tua zona
            </a>
          </div>

          <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-lg aspect-[4/3] lg:aspect-square">
            <iframe
              title="Mappa zona di copertura Parma Piacenza Modena"
              src="https://www.google.com/maps?q=Parma%2C+Piacenza%2C+Modena%2C+Italia&z=8&output=embed"
              className="w-full h-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="coverage-map"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// CONTACT FORM
// ============================================================
function ContactForm() {
  const [form, setForm] = useState({ name: "", phone: "", city: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Inserisci almeno nome e telefono.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact-request`, form);
      toast.success("Richiesta ricevuta. Ti ricontattiamo subito!");
      setForm({ name: "", phone: "", city: "", service: "", message: "" });
    } catch (err) {
      toast.error("Errore nell'invio. Chiama direttamente o riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="richiedi"
      className="bg-slate-50 py-16 md:py-24"
      data-testid="contact-form-section"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-start">
          <div>
            <span className="text-[#FF6A2A] font-bold uppercase tracking-[0.2em] text-xs">
              Richiamata Veloce
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-[#0B1F33] mt-3 mb-4 leading-tight">
              Non puoi chiamare ora?<br />Ti richiamiamo noi.
            </h2>
            <p className="text-slate-600 text-base mb-6 leading-relaxed">
              Lascia i tuoi contatti, un tecnico ti chiamerà entro pochi minuti per valutare la tua emergenza.
            </p>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2 text-slate-700">
                <Phone className="w-4 h-4 text-[#FF6A2A]" />
                <a href={telLink} className="font-semibold hover:underline" data-testid="contact-info-phone">
                  {PHONE_DISPLAY}
                </a>
              </div>
              <div className="flex items-center gap-2 text-slate-700">
                <MessageCircle className="w-4 h-4 text-[#25D366]" />
                <a
                  href={whatsappLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold hover:underline"
                  data-testid="contact-info-whatsapp"
                >
                  WhatsApp 24/7
                </a>
              </div>
              <div className="flex items-center gap-2 text-slate-700">
                <span className="w-4 h-4 inline-flex items-center justify-center text-[#0B1F33] font-black">@</span>
                <span className="font-semibold">{EMAIL}</span>
              </div>
            </div>
          </div>

          <form
            onSubmit={submit}
            className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-lg"
            data-testid="contact-form"
          >
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Nome</label>
                <Input
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Mario Rossi"
                  className="border-slate-200 focus-visible:ring-[#FF6A2A]"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Telefono</label>
                <Input
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+39 333 1234567"
                  className="border-slate-200 focus-visible:ring-[#FF6A2A]"
                  data-testid="contact-phone-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Città</label>
                  <Input
                    value={form.city}
                    onChange={update("city")}
                    placeholder="Parma"
                    className="border-slate-200 focus-visible:ring-[#FF6A2A]"
                    data-testid="contact-city-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Servizio</label>
                  <Input
                    value={form.service}
                    onChange={update("service")}
                    placeholder="es. Idraulico"
                    className="border-slate-200 focus-visible:ring-[#FF6A2A]"
                    data-testid="contact-service-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Descrivi il problema</label>
                <Textarea
                  rows={3}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Breve descrizione dell'emergenza..."
                  className="border-slate-200 focus-visible:ring-[#FF6A2A] resize-none"
                  data-testid="contact-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#FF6A2A] hover:bg-[#E55A1A] text-white font-bold text-base py-6 rounded-xl shadow-md"
                data-testid="contact-submit-button"
              >
                {loading ? "Invio..." : (
                  <>
                    Richiamami Subito <ArrowRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
              <p className="text-[11px] text-slate-500 text-center">
                Inviando accetti di essere ricontattato per gestire la tua emergenza.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// FINAL CTA
// ============================================================
function FinalCTA() {
  return (
    <section
      className="relative py-20 md:py-28 bg-[#0B1F33] text-white overflow-hidden"
      data-testid="final-cta-section"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[#0B1F33] via-[#12304A] to-[#0B1F33]" />
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#FF6A2A]/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-[#FF6A2A]/10 rounded-full blur-3xl" />

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#FF6A2A]/15 border border-[#FF6A2A]/30 mb-6">
          <AlertTriangle className="w-7 h-7 text-[#FF6A2A]" strokeWidth={2.2} />
        </div>
        <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mb-5">
          Hai un'emergenza?<br />
          <span className="text-[#FF6A2A]">Non aspettare.</span> Chiama subito.
        </h2>
        <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
          Un nostro tecnico sarà da te entro 30–60 minuti. Disponibili 24 ore su 24, 365 giorni l'anno.
        </p>

        <a
          href={telLink}
          className="inline-flex items-center justify-center gap-4 bg-[#FF6A2A] hover:bg-[#E55A1A] text-white font-bold text-xl sm:text-2xl py-6 px-10 rounded-2xl shadow-2xl shadow-[#FF6A2A]/30 transition-all hover:-translate-y-1"
          data-testid="final-cta-call-button"
        >
          <Phone className="w-7 h-7" strokeWidth={2.6} />
          {PHONE_DISPLAY}
        </a>

        <div className="mt-6">
          <a
            href={whatsappLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 text-white/80 hover:text-white font-semibold transition-colors"
            data-testid="final-cta-whatsapp-link"
          >
            <MessageCircle className="w-5 h-5" /> oppure scrivici su WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// FOOTER
// ============================================================
function Footer() {
  return (
    <footer className="bg-[#0B1F33] text-white/80 pt-14 pb-28 md:pb-10 border-t border-white/5" data-testid="site-footer">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#FF6A2A] text-white">
                <ShieldCheck className="w-5 h-5" strokeWidth={2.5} />
              </span>
              <div className="font-display font-black text-base uppercase tracking-tight text-white leading-tight">
                Pronto Intervento <span className="text-[#FF6A2A]">24H</span>
              </div>
            </div>
            <p className="text-sm leading-relaxed max-w-sm">
              Il tuo punto di riferimento per interventi idraulici, elettrici, spurghi e apertura porte blindate. Sempre disponibili, sempre al tuo fianco.
            </p>
            <div className="inline-flex items-center gap-2 mt-5 bg-white/5 border border-white/10 text-white font-bold uppercase tracking-wider text-[11px] px-3 py-2 rounded-lg">
              <Clock className="w-3.5 h-3.5 text-[#FF6A2A]" strokeWidth={2.5} />
              Disponibili 24/7
            </div>
          </div>

          {/* Servizi */}
          <div>
            <h4 className="font-display font-bold text-[#FF6A2A] uppercase text-xs tracking-wider mb-4">Servizi</h4>
            <ul className="space-y-2 text-sm">
              <li><a href="#servizi" className="hover:text-white">Idraulico</a></li>
              <li><a href="#servizi" className="hover:text-white">Elettricista</a></li>
              <li><a href="#servizi" className="hover:text-white">Spurghi</a></li>
              <li><a href="#servizi" className="hover:text-white">Apertura Porte Blindate</a></li>
            </ul>
          </div>

          {/* Link utili */}
          <div>
            <h4 className="font-display font-bold text-[#FF6A2A] uppercase text-xs tracking-wider mb-4">Link Utili</h4>
            <ul className="space-y-2 text-sm">
              <li><a href="#prezzi" className="hover:text-white">Prezzi</a></li>
              <li><a href="#zone" className="hover:text-white">Zone di intervento</a></li>
              <li><a href="#giulio" className="hover:text-white">Chi siamo</a></li>
              <li><a href="#richiedi" className="hover:text-white">Contatti</a></li>
            </ul>
          </div>

          {/* Contatti + WhatsApp */}
          <div>
            <h4 className="font-display font-bold text-[#FF6A2A] uppercase text-xs tracking-wider mb-4">Contatti</h4>
            <ul className="space-y-3 text-sm mb-5">
              <li className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-[#FF6A2A] flex-shrink-0 mt-0.5" />
                <a href={telLink} className="hover:text-white font-semibold" data-testid="footer-phone">
                  {PHONE_DISPLAY}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-4 h-4 inline-flex items-center justify-center text-[#FF6A2A] font-black flex-shrink-0 mt-0.5">@</span>
                <span>{EMAIL}</span>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-[#FF6A2A] flex-shrink-0 mt-0.5" />
                <span>Parma, Piacenza, Modena</span>
              </li>
              <li className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-[#FF6A2A] flex-shrink-0 mt-0.5" />
                <span>Disponibili 24 ore su 24, 7 giorni su 7</span>
              </li>
            </ul>

            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1DA851] text-white font-bold px-4 py-3 rounded-lg transition-all w-full justify-center"
              data-testid="footer-whatsapp"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={2.5} />
              <span className="leading-tight text-left">
                <span className="block text-sm">WhatsApp</span>
                <span className="block text-[11px] opacity-90 font-semibold">Immediato</span>
              </span>
            </a>
          </div>
        </div>

        {/* Legal row */}
        <div className="border-t border-white/10 mt-12 pt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[11px] text-white/55">
          <div>
            © {new Date().getFullYear()} {LEGAL_NAME} · P.IVA {VAT_NUMBER} · {COMPANY_ADDRESS} · PEC: {PEC_EMAIL}
          </div>
          <div className="flex gap-5">
            <a href="#" className="hover:text-white">Privacy Policy</a>
            <a href="#" className="hover:text-white">Cookie Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ============================================================
// FLOATING WHATSAPP
// ============================================================
function FloatingWhatsApp() {
  return (
    <a
      href={whatsappLink()}
      target="_blank"
      rel="noopener noreferrer"
      className="wa-pulse hidden md:flex fixed bottom-6 right-6 z-30 items-center justify-center w-16 h-16 bg-[#25D366] hover:bg-[#1DA851] text-white rounded-full shadow-2xl transition-all hover:scale-105"
      aria-label="Apri chat WhatsApp"
      data-testid="floating-whatsapp"
    >
      <MessageCircle className="w-7 h-7" strokeWidth={2.5} />
    </a>
  );
}

// ============================================================
// STICKY MOBILE BAR
// ============================================================
function StickyMobileBar() {
  return (
    <div
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 grid grid-cols-2 border-t border-[#0B1F33] shadow-[0_-4px_20px_rgba(0,0,0,0.15)]"
      data-testid="sticky-mobile-bar"
    >
      <a
        href={telLink}
        className="flex items-center justify-center gap-2 bg-[#FF6A2A] text-white font-bold py-4 text-base"
        data-testid="mobile-bar-call"
      >
        <Phone className="w-5 h-5" strokeWidth={2.8} /> Chiama
      </a>
      <a
        href={whatsappLink()}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 bg-[#25D366] text-white font-bold py-4 text-base"
        data-testid="mobile-bar-whatsapp"
      >
        <MessageCircle className="w-5 h-5" strokeWidth={2.5} /> WhatsApp
      </a>
    </div>
  );
}

// ============================================================
// PAGE
// ============================================================
export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="landing-page">
      <Header />
      <main>
        <Hero />
        <ServicesSection />
        <PricingSection />
        <HowItWorks />
        <TechSection />
        <ReviewsSection />
        <CoverageSection />
        <ContactForm />
        <FinalCTA />
      </main>
      <Footer />
      <FloatingWhatsApp />
      <StickyMobileBar />
    </div>
  );
}
